// eca-webapp and eca-mobile-expo copy this file; change all three together and keep the ECA-5875 vectors passing.
import { toEventTime } from 'utils';

const MINUTE = 60 * 1000;
const MINUTES_PER_DAY = 24 * 60;

export const COUNTDOWN_VARIANTS = ['plain', 'flip', 'aurora', 'neon'];

export const COUNTDOWN_VARIANT_COLORS = {
	plain: {
		digitColor: '#151a21',
		labelColor: '#6b7076',
		backgroundColor: '#ffffff',
		tileColor: null,
		accentColor: null,
	},
	flip: {
		digitColor: '#f4f1ea',
		labelColor: '#4b5058',
		backgroundColor: '#ffffff',
		tileColor: '#1d1f23',
		accentColor: null,
	},
	aurora: {
		digitColor: '#ffffff',
		labelColor: '#ffffff',
		backgroundColor: '#6d28d9',
		tileColor: null,
		accentColor: '#f97316',
	},
	neon: {
		digitColor: '#fdf4ff',
		labelColor: '#a5f3fc',
		backgroundColor: '#0b0f1a',
		tileColor: null,
		accentColor: '#22d3ee',
	},
};

export const COUNTDOWN_COLOR_FIELDS = {
	plain: [
		{ prop: 'digitColor', label: 'Numbers' },
		{ prop: 'labelColor', label: 'Text' },
		{ prop: 'backgroundColor', label: 'Background', allowTransparent: true },
	],
	flip: [
		{ prop: 'tileColor', label: 'Tiles' },
		{ prop: 'digitColor', label: 'Numbers' },
		{ prop: 'labelColor', label: 'Text' },
		{ prop: 'backgroundColor', label: 'Background', allowTransparent: true },
	],
	aurora: [
		{ prop: 'backgroundColor', label: 'Gradient start', allowTransparent: true },
		{ prop: 'accentColor', label: 'Gradient end', hiddenWhenTransparent: true },
		{ prop: 'digitColor', label: 'Numbers' },
		{ prop: 'labelColor', label: 'Text' },
	],
	neon: [
		{ prop: 'backgroundColor', label: 'Background', allowTransparent: true },
		{ prop: 'digitColor', label: 'Numbers' },
		{ prop: 'accentColor', label: 'Glow' },
		{ prop: 'labelColor', label: 'Text' },
	],
};

export const TRANSPARENT = 'transparent';

export const asText = value => (typeof value === 'string' ? value.trim() : '');

export const COUNTDOWN_DEFAULT_PROPS = {
	variant: 'plain',
	targetTime: null,
	title: '',
	titlePosition: 'above',
	endMessage: '',
	showTargetTime: false,
	...COUNTDOWN_VARIANT_COLORS.plain,
};

export const resolveCountdownVariant = props =>
	COUNTDOWN_VARIANTS.includes(props?.variant) ? props.variant : 'plain';

export const resolveCountdownColor = (props, prop) =>
	props?.[prop] || COUNTDOWN_VARIANT_COLORS[resolveCountdownVariant(props)][prop];

export const resolveCountdownTarget = props => {
	const target = Date.parse(props?.targetTime);
	return Number.isNaN(target) ? null : target;
};

const minutesLeft = (targetMs, nowMs) => Math.ceil((targetMs - nowMs) / MINUTE);

export const getCountdownParts = (targetMs, nowMs) => {
	const totalMinutes = minutesLeft(targetMs, nowMs);
	if (!(totalMinutes > 0)) {
		return {
			mode: 'ended',
			units: [
				{ unit: 'hrs', value: 0 },
				{ unit: 'min', value: 0 },
			],
		};
	}
	if (totalMinutes >= MINUTES_PER_DAY) {
		return {
			mode: 'dh',
			units: [
				{ unit: 'days', value: Math.floor(totalMinutes / MINUTES_PER_DAY) },
				{ unit: 'hrs', value: Math.floor((totalMinutes % MINUTES_PER_DAY) / 60) },
			],
		};
	}
	if (totalMinutes >= 60) {
		return {
			mode: 'hm',
			units: [
				{ unit: 'hrs', value: Math.floor(totalMinutes / 60) },
				{ unit: 'min', value: totalMinutes % 60 },
			],
		};
	}
	return { mode: 'm', units: [{ unit: 'min', value: totalMinutes }] };
};

export const msUntilNextChange = (targetMs, nowMs) => {
	const totalMinutes = minutesLeft(targetMs, nowMs);
	if (!(totalMinutes > 0)) return null;
	const nextTotalMinutes =
		totalMinutes >= MINUTES_PER_DAY ? Math.floor(totalMinutes / 60) * 60 - 1 : totalMinutes - 1;
	return targetMs - nowMs - nextTotalMinutes * MINUTE;
};

const UNIT_NAMES = {
	days: { one: 'day', other: 'days' },
	hrs: { one: 'hour', other: 'hours' },
	min: { one: 'min', other: 'min' },
};

export const formatUnitLabel = (unit, value) => UNIT_NAMES[unit][value === 1 ? 'one' : 'other'];

export const formatCountdownTarget = (event, targetMs) =>
	toEventTime(event, targetMs).format('ddd, MMM D · h:mm A z');
